
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Exercise } from '@/models/LearnTypes';
import { cn } from '@/lib/utils';
import { CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import BaseExercise from './BaseExercise';
import { useRecyclingPhase } from '@/hooks/exercise/useRecyclingPhase';
import { usePerfectLevel } from '@/contexts/PerfectLevelContext';

interface SpellingExerciseProps {
  currentExercise: Exercise;
  isAnimating: boolean;
  showAnswer: boolean;
  userInput: string;
  isInputCorrect: boolean | null;
  onUserInputChange: (value: string) => void;
  onCheck: () => void;
  onNext: () => void;
  isLastExercise: boolean;
  nextCard: Exercise | null;
  initialAnimation?: boolean;
  onClose?: () => void;
  // Perfect level tracking
  isPerfect?: boolean;
  onImperfect?: () => void;
  // Progress tracking
  isRecycledWord?: boolean;
  exercises?: Exercise[];
  totalOriginalExercises?: number;
  wordsToReview?: string[];
  // Activity logging
  onLogActivity?: (activityType: string, exercise: Exercise) => void;
}

const SpellingExercise: React.FC<SpellingExerciseProps> = ({
  currentExercise,
  isAnimating,
  showAnswer,
  userInput,
  isInputCorrect,
  onUserInputChange,
  onCheck,
  onNext,
  isLastExercise,
  nextCard,
  initialAnimation = false,
  onClose,
  // Perfect level tracking with default value
  isPerfect = true,
  onImperfect,
  // Progress tracking
  isRecycledWord = false,
  exercises = [],
  totalOriginalExercises = 0,
  wordsToReview = [],
  onLogActivity
}) => {
  const [showExcellentAnimation, setShowExcellentAnimation] = useState(false);
  
  // Track whether this exercise has already been marked as imperfect
  const hasBeenMarkedImperfect = useRef(false);
  // Track if activity has been logged for this exercise to prevent duplicate logging
  const hasLoggedActivity = useRef(false);
  
  // Use the recycling phase hook for more accurate detection
  const { isInRecyclingPhase } = useRecyclingPhase({
    exercises: exercises,
    currentExerciseIndex: currentExercise ? exercises.findIndex(e => e.id === currentExercise.id) : 0,
    totalOriginalExercises,
    wordsToReview,
    currentExerciseId: currentExercise?.id
  });
  
  // Log recycling status for debugging
  useEffect(() => {
    // Determine recycling status in multiple ways for debugging
    const inReviewList = wordsToReview.includes(currentExercise?.id || '');
    const globalRecyclingFlag = (window as any).__HAD_RECYCLED_WORDS__ === true;
    const isOriginalZero = totalOriginalExercises === 0;
    const manualFlag = isRecycledWord;
    
    console.log(`[SpellingExercise] Word: ${currentExercise?.word}, isInRecyclingPhase: ${isInRecyclingPhase}, index: ${exercises.findIndex(e => e.id === currentExercise?.id)}, totalOriginal: ${totalOriginalExercises}`);
    console.log(`[SpellingExercise] Recycling detection: hookValue=${isInRecyclingPhase}, inReview=${inReviewList}, globalFlag=${globalRecyclingFlag}, isOriginalZero=${isOriginalZero}, propFlag=${manualFlag}`);
  }, [
    currentExercise?.id, 
    currentExercise?.word, 
    exercises, 
    isInRecyclingPhase,
    isRecycledWord, 
    totalOriginalExercises, 
    wordsToReview
  ]);
  
  // Show "Excellent" animation when answer is correct
  useEffect(() => {
    if (isInputCorrect === true && showAnswer) {
      setShowExcellentAnimation(true);
      
      // Show "Excellent!" animation for a brief moment before allowing continuation
      setTimeout(() => {
        setShowExcellentAnimation(false);
      }, 800); // 800ms delay matching other exercise types
    }
  }, [isInputCorrect, showAnswer]);
  
  // Input reference for controlled focus management
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Reset the input field focus when the component changes
  useEffect(() => {
    // Reset the input when moving to a new exercise, but only if the previous answer was shown
    // This preserves misspelled words until the user sees the correct answer
    const isNewExercise = prevExerciseRef.current !== currentExercise.id;
    prevExerciseRef.current = currentExercise.id;
    
    if (isNewExercise && showAnswer) {
      onUserInputChange('');
    }
    
    // Only set focus when the component first loads, exercise changes, or when answer is hidden
    // Do not focus on the input when showing the answer
    const timer = setTimeout(() => {
      if (inputRef.current && !isAnimating && !showAnswer) {
        inputRef.current.focus();
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [currentExercise, onUserInputChange, showAnswer, isAnimating]);
  
  // Keep track of the previous exercise to detect changes
  const prevExerciseRef = useRef<string>(currentExercise?.id || '');
  
  // Get access to the perfect level context
  const { markLevelImperfect } = usePerfectLevel();

  // Dedicated function to handle imperfect status - similar to MatchExercise
  const handleImperfect = () => {
    // More robust level index determination
    let levelIndex = -1;
    
    // Method 1: Try to extract from URL
    if (window.location.pathname.includes('/learn/')) {
      const pathParts = window.location.pathname.split('/');
      const potentialIndex = Number(pathParts[pathParts.length - 1]);
      if (!isNaN(potentialIndex)) {
        levelIndex = potentialIndex;
      }
    }
    
    // Method 2: Fallback to local storage if it exists
    if (levelIndex < 0) {
      try {
        const storedLevelIndex = localStorage.getItem('current_level_index');
        if (storedLevelIndex !== null) {
          levelIndex = Number(storedLevelIndex);
        }
      } catch (e) {
        console.error('Error reading level index from localStorage', e);
      }
    }
    
    // Even if we can't determine the level index, still log the activity
    if (!hasBeenMarkedImperfect.current) {
      // Log whether we found a valid level index
      if (levelIndex >= 0) {
        console.log(`✅ [SpellingExercise] Found level index: ${levelIndex}`);
      } else {
        console.warn(`⚠️ [SpellingExercise] Could not determine level index, using fallback approach`);
      }
      console.log(`❌ [SpellingExercise] Marking level ${levelIndex} as imperfect - incorrect spelling for word: ${currentExercise.word}`);
      
      // 1. Use context to mark the level as imperfect (only if we have a valid level index)
      if (levelIndex >= 0) {
        markLevelImperfect(levelIndex);
      } else {
        console.warn(`⚠️ [SpellingExercise] Cannot mark level as imperfect - unknown level index`);
      }
      
      // 2. Set global flag for legacy compatibility and activity logging  
      (window as any).__SPELLING_EXERCISE_HAD_MISTAKE__ = true;
      (window as any).__HAD_RECYCLED_WORDS__ = true; // Used by activity logging
      (window as any).__PERFECT_LEVEL_STATUS__ = false;
      
      // 3. Store in localStorage for persistence
      try {
        localStorage.setItem(`spelling_exercise_had_mistake_level_${levelIndex}`, 'true');
        localStorage.setItem('spelling_exercise_had_mistake', 'true');
        localStorage.setItem('perfect_level_status', 'false');
        localStorage.setItem('had_recycled_words', 'true');
        console.log(`💾 [SpellingExercise] Set level-specific flags for level ${levelIndex}`);
      } catch (e) {
        console.error('Error storing spelling mistake in localStorage', e);
      }
      
      // 4. Activity logging is now handled in a separate useEffect to ensure reliability
      
      // 5. Maintain backward compatibility with onImperfect callback
      if (onImperfect) {
        onImperfect();
      }
      
      hasBeenMarkedImperfect.current = true;
    }
  };

  // Handle incorrect spelling - using the dedicated handleImperfect function
  useEffect(() => {
    if (isInputCorrect === false && isPerfect) {
      handleImperfect();
    }
  }, [isInputCorrect, isPerfect, currentExercise.id]);
  
  // Dedicated effect for activity logging that's independent of level index determination
  // This ensures activity logs are created even if perfect level tracking has issues
  useEffect(() => {
    if (isInputCorrect === false && !hasLoggedActivity.current && onLogActivity && currentExercise) {
      console.log(`📊 [SpellingExercise] Dedicated activity logging for failed word: ${currentExercise.word}`);
      onLogActivity('failed_word', currentExercise);
      hasLoggedActivity.current = true;
    }
  }, [isInputCorrect, currentExercise, onLogActivity]);
  
  // Reset the imperfect tracking when exercise changes
  useEffect(() => {
    console.log(`🔄 [SpellingExercise] Exercise changed to ${currentExercise.word}, resetting imperfect tracking`);
    hasBeenMarkedImperfect.current = false;
    hasLoggedActivity.current = false;
    
    // Also initialize the status flags for a clean state
    const levelIndex = window.location.pathname.includes('/learn/') ?
      Number(window.location.pathname.split('/').pop()) : -1;
    
    if (levelIndex >= 0) {
      console.log(`🔄 [SpellingExercise] Initializing perfect tracking for level ${levelIndex}`);
      
      // We don't reset global flags here because that could wipe out mistakes from previous exercises
      // We only reset our local tracking for THIS specific exercise
    }
  }, [currentExercise.id, currentExercise.word]);
  
  // Combined recycling indicator based on multiple sources
  const showRecyclingIndicator = isInRecyclingPhase || isRecycledWord;
  
  const renderExerciseContent = () => (
    <div className="space-y-4">
      <div className="p-6 bg-muted/30 rounded-lg text-center">
        <h3 className="text-2xl font-semibold mb-2">{currentExercise.translation}</h3>
        <p className="text-muted-foreground">
          Type the French word
        </p>
      </div>
      
      <div className="relative flex justify-center">
        <Input
          ref={inputRef}
          value={userInput}
          onChange={(e) => !showAnswer && onUserInputChange(e.target.value)}
          placeholder="Type the French word..."
          className={cn(
            "text-center text-lg max-w-[85%]",
            isInputCorrect === true && "border-green-500 bg-green-50",
            isInputCorrect === false && "border-red-500 bg-red-50",
            showAnswer && "cursor-default"
          )}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (showAnswer) {
                onNext();
              } else {
                onCheck();
              }
            }
          }}
          // Disable the input when showing answer or during animations
          // This keeps the text visible but prevents editing
          disabled={showAnswer || isAnimating}
          // Remove focus outline when showing answer
          style={showAnswer ? { caretColor: 'transparent' } : {}}
          data-recycled={showRecyclingIndicator}
        />
        
        {isInputCorrect === true && showAnswer && (
          <div className="absolute right-[10%] top-1/2 -translate-y-1/2 text-green-500 animate-scale-in">
            <CheckCircle className="h-6 w-6" />
          </div>
        )}
        
        {isInputCorrect === false && showAnswer && (
          <div className="absolute right-[10%] top-1/2 -translate-y-1/2 text-red-500 animate-scale-in">
            <XCircle className="h-6 w-6" />
          </div>
        )}
      </div>
      
      {showAnswer && isInputCorrect === false && (
        <div className="p-4 bg-muted rounded-lg mt-4 animate-fade-in">
          <p className="font-medium text-center">
            <span className="text-muted-foreground">Correct answer: </span>
            <span className="text-lg">{currentExercise.word}</span>
          </p>
        </div>
      )}
    </div>
  );

  const renderActionButtons = () => {
    if (!showAnswer) {
      return (
        <Button
          className="w-full mt-4"
          onClick={onCheck}
        >
          Check
        </Button>
      );
    } else if (isInputCorrect === false) {
      return (
        <Button
          className="w-full mt-4"
          onClick={onNext}
          variant="default"
        >
          {isLastExercise ? 'Complete Level' : 'Continue'}
        </Button>
      );
    } else if (isInputCorrect === true) {
      return (
        <Button
          className={cn(
            "w-full mt-4 transition-all duration-200",
            showExcellentAnimation && "bg-green-500 hover:bg-green-600"
          )}
          onClick={onNext}
        >
          {showExcellentAnimation ? "Excellent!" : (isLastExercise ? 'Complete Level' : 'Continue')}
        </Button>
      );
    }
    return null;
  };

  return (
    <BaseExercise
      currentExercise={currentExercise}
      isAnimating={isAnimating}
      onNext={onNext}
      isLastExercise={isLastExercise}
      renderExerciseContent={renderExerciseContent}
      renderActionButtons={renderActionButtons}
      initialAnimation={initialAnimation}
      onClose={onClose}
      data-recycled-word={showRecyclingIndicator}
    />
  );
};

export default SpellingExercise;
